<div
  class="rounded-2xl border border-neutral-a4 bg-neutral-a2 shadow-2xs overflow-hidden w-full transition-all"
>
  <!-- Accordion Drawer Header -->
  <button
    type="button"
    class="w-full text-left p-5 sm:p-6 flex items-center justify-between gap-4 cursor-pointer select-none transition-colors hover:bg-neutral-a3/40 focus:outline-none"
    (click)="toggleOpen()"
  >
    <div class="flex items-center gap-4 min-w-0">
      <div
        class="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary font-bold"
      >
        <span class="material-icons text-2xl">receipt_long</span>
      </div>
      <div class="min-w-0">
        <div class="flex items-center gap-2.5 flex-wrap">
          <h3 class="text-base sm:text-lg font-bold text-neutral-12 tracking-tight">
            Historique des abonnements
          </h3>
          @if (total() > 0) {
            <span class="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-neutral-a3 text-neutral-11">
              {{ total() }} {{ total() > 1 ? 'abonnements' : 'abonnement' }}
            </span>
          }
        </div>
        <p class="text-xs sm:text-sm text-neutral-11 mt-0.5 truncate">
          Consulter l'historique complet de vos formules et transactions associées
        </p>
      </div>
    </div>

    <div class="flex items-center gap-2 shrink-0">
      <span
        class="flex h-8 w-8 items-center justify-center rounded-full bg-neutral-a3 text-neutral-11 transition-transform duration-200"
        [class.rotate-180]="isOpen()"
      >
        <span class="material-icons text-xl">expand_more</span>
      </span>
    </div>
  </button>

  <!-- Accordion Drawer Content -->
  @if (isOpen()) {
    <div class="border-t border-neutral-a4 p-5 sm:p-6 bg-neutral-1/40 flex flex-col gap-5">
      @if (loading() && history().length === 0) {
        <div
          class="flex flex-col items-center justify-center p-10 rounded-xl border border-neutral-a3 bg-neutral-a2"
        >
          <mat-spinner diameter="32" strokeWidth="3"></mat-spinner>
          <span class="mt-3 text-sm text-neutral-11">Chargement de votre historique...</span>
        </div>
      } @else if (history().length === 0) {
        <div
          class="flex flex-col items-center justify-center p-10 text-center rounded-xl border border-neutral-a3 bg-neutral-a2"
        >
          <div
            class="flex h-11 w-11 items-center justify-center rounded-xl bg-neutral-a3 text-neutral-11 mb-2.5"
          >
            <span class="material-icons text-2xl">receipt_long</span>
          </div>
          <h4 class="text-sm font-bold text-neutral-12">Aucun historique disponible</h4>
          <p class="text-xs text-neutral-11 mt-1 max-w-sm">
            Vous n'avez pas encore d'historique de souscription ou de paiement sur votre compte.
          </p>
        </div>
      } @else {
        <div class="flex flex-col gap-4">
          @for (sub of history(); track sub._id) {
            <div
              class="rounded-xl border border-neutral-a3 bg-neutral-a2 p-4 sm:p-5 shadow-2xs transition-all"
            >
              <!-- Subscription Header -->
              <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div class="flex items-center gap-3">
                  <div
                    class="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary font-bold text-base"
                  >
                    <span class="material-icons text-lg">workspace_premium</span>
                  </div>
                  <div>
                    <div class="flex items-center gap-2 flex-wrap">
                      <h4 class="text-base font-extrabold text-neutral-12">
                        {{ sub.plan.name }}
                      </h4>
                      @if (sub.plan.price) {
                        <span
                          class="text-xs font-semibold text-neutral-11 bg-neutral-a3 px-2 py-0.5 rounded-md"
                        >
                          {{ sub.plan.price | priceDisplay: sub.plan.currency }}
                          @if (sub.plan.billingCycle) {
                            / {{ sub.plan.billingCycle }}
                          }
                        </span>
                      }
                    </div>
                    <div class="text-xs text-neutral-11 mt-0.5">
                      Souscrit le {{ sub.createdAt | date: 'dd MMMM yyyy' : undefined : 'fr-FR' }}
                    </div>
                  </div>
                </div>

                <div class="flex items-center gap-2 self-start sm:self-center">
                  <aura-subscription-status-chip [status]="sub.status" />

                  @if (sub.cancelAtPeriodEnd) {
                    <span
                      class="rounded-full px-2.5 py-0.5 text-xs font-semibold flex items-center gap-1.5 border bg-amber-3 text-amber-11 border-amber-5"
                    >
                      <span class="h-1.5 w-1.5 rounded-full bg-amber-9"></span>
                      Annulation différée
                    </span>
                  }
                </div>
              </div>

              <!-- Subscription Period Details -->
              <div
                class="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3 p-3 rounded-lg bg-neutral-1 border border-neutral-a3 text-xs"
              >
                <div>
                  <span class="text-neutral-11 font-medium block">Période de validité</span>
                  <span class="font-semibold text-neutral-12">
                    Du {{ sub.currentPeriodStart | date: 'dd MMM yyyy' : undefined : 'fr-FR' }} au
                    {{ sub.currentPeriodEnd | date: 'dd MMM yyyy' : undefined : 'fr-FR' }}
                  </span>
                </div>
                @if (sub.metadata?.stripe?.subscriptionId) {
                  <div>
                    <span class="text-neutral-11 font-medium block">ID Stripe</span>
                    <span class="font-mono text-neutral-11 font-medium">
                      {{ sub.metadata?.stripe?.subscriptionId }}
                    </span>
                  </div>
                }
              </div>

              <!-- Associated Transactions Section -->
              <div class="mt-4 pt-3 border-t border-neutral-a3">
                <div class="flex items-center justify-between" [class.mb-3]="isExpanded(sub._id)">
                  <div class="flex items-center gap-2">
                    <span class="material-icons text-sm text-neutral-11">receipt</span>
                    <span class="text-xs font-bold text-neutral-11 uppercase tracking-wider">
                      Transactions associées
                      @if (sub.transactions?.length) {
                        ({{ sub.transactions?.length }})
                      }
                    </span>
                  </div>

                  @if (sub.transactions?.length) {
                    <button
                      mat-button
                      class="!text-xs !py-0 !px-2 !min-h-7 link"
                      (click)="toggleExpanded(sub._id)"
                    >
                      <span class="material-icons text-sm mr-1">
                        {{ isExpanded(sub._id) ? 'expand_less' : 'expand_more' }}
                      </span>
                      {{ isExpanded(sub._id) ? 'Masquer' : 'Afficher' }}
                    </button>
                  }
                </div>

                @if (isExpanded(sub._id)) {
                  @if (sub.transactions && sub.transactions.length > 0) {
                    <div class="overflow-x-auto rounded-lg border border-neutral-a3">
                      <table class="w-full text-left text-xs border-collapse">
                        <thead>
                          <tr
                            class="bg-neutral-a3/50 text-neutral-11 border-b border-neutral-a3 font-semibold"
                          >
                            <th class="p-2.5">Date</th>
                            <th class="p-2.5">Montant</th>
                            <th class="p-2.5">Statut</th>
                            <th class="p-2.5">Réf. Facture</th>
                          </tr>
                        </thead>
                        <tbody class="divide-y divide-neutral-a3 bg-neutral-1">
                          @for (tx of sub.transactions; track tx._id) {
                            <tr class="hover:bg-neutral-a2 transition-colors">
                              <td class="p-2.5 font-medium text-neutral-12 whitespace-nowrap">
                                {{ tx.date | date: 'dd/MM/yyyy HH:mm' : undefined : 'fr-FR' }}
                              </td>
                              <td class="p-2.5 font-bold text-neutral-12 whitespace-nowrap">
                                {{ tx.amount | priceDisplay: tx.currency }}
                              </td>
                              <td class="p-2.5 whitespace-nowrap">
                                <span
                                  class="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold border"
                                  [class]="getTransactionStatusBadgeClass(tx.status)"
                                >
                                  <span class="material-icons text-xs">
                                    {{ getTransactionStatusIcon(tx.status) }}
                                  </span>
                                  {{ tx.status | transactionStatusDisplay }}
                                </span>
                              </td>
                              <td class="p-2.5 font-mono text-neutral-11 whitespace-nowrap">
                                {{ tx.metadata?.stripe?.invoiceId || tx._id }}
                              </td>
                            </tr>
                          }
                        </tbody>
                      </table>
                    </div>
                  } @else {
                    <div
                      class="p-2.5 text-xs text-neutral-11 bg-neutral-1 rounded-lg border border-neutral-a3 text-center"
                    >
                      Aucune transaction enregistrée pour cet abonnement.
                    </div>
                  }
                }
              </div>
            </div>
          }

          @if (loadingMore()) {
            <div
              class="flex items-center justify-center p-3.5 rounded-xl border border-neutral-a3 bg-neutral-a2 gap-3"
            >
              <mat-spinner diameter="18" strokeWidth="2"></mat-spinner>
              <span class="text-xs text-neutral-11">Chargement des abonnements suivants...</span>
            </div>
          }

          @if (hasMore() && !loadingMore()) {
            <div class="flex justify-center mt-1">
              <button mat-stroked-button class="!text-xs" (click)="loadMore.emit()">
                <span class="material-icons text-sm mr-1.5">expand_more</span>
                Charger les abonnements précédents
              </button>
            </div>
          }

          <!-- Intersection Observer Sentinel for Auto Infinite Scroll -->
          <div #sentinel class="h-1 w-full opacity-0 pointer-events-none"></div>
        </div>
      }
    </div>
  }
</div>
